import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Package, Truck, CheckCircle2, Ban } from 'lucide-react';
import api from '../../../services/api';
import usePaginatedResource from '../../../hooks/usePaginatedResource';
import useResourceMutation from '../../../hooks/useResourceMutation';
import PageHeader from '../../../components/shared/PageHeader/PageHeader';
import DataTable, { Column } from '../../../components/shared/DataTable/DataTable';
import Button from '../../../components/shared/Button/Button';
import StatusBadge from '../../../components/shared/StatusBadge/StatusBadge';
import ConfirmDialog from '../../../components/shared/ConfirmDialog/ConfirmDialog';
import Modal from '../../../components/shared/Modal/Modal';
import FormField from '../../../components/shared/FormField/FormField';
import Input from '../../../components/shared/Input/Input';
import SearchableSelect from '../../../components/shared/SearchableSelect/SearchableSelect';
import StatStrip from '../../../components/shared/StatStrip/StatStrip';
import { createParcel, updateParcel } from '../../../services/parcels';
import { listTrips } from '../../../services/trips';
import { listStops } from '../../../services/stops';
import { ParcelHistoryModal } from './ParcelHistoryModal';
import type { Parcel, ParcelStatus, Trip, Stop } from '../../../types/api';

const NEXT_STATUS: Partial<Record<ParcelStatus, { status: ParcelStatus; label: string }>> = {
  received:   { status: 'in_transit', label: 'Dispatch' },
  in_transit: { status: 'arrived',    label: 'Mark arrived' },
  arrived:    { status: 'delivered',  label: 'Mark delivered' },
  delivered:  { status: 'picked',     label: 'Mark picked' },
};

interface ParcelForm {
  sender_name: string; sender_phone: string;
  sender_id_number: string; sender_kra_pin: string;
  recipient_name: string; recipient_phone: string;
  from_location: string; to_location: string;
  trip: string; description: string;
  declared_value: string; weight: string; shipping_fee: string;
}
const emptyParcel: ParcelForm = {
  sender_name: '', sender_phone: '',
  sender_id_number: '', sender_kra_pin: '',
  recipient_name: '', recipient_phone: '',
  from_location: '', to_location: '',
  trip: '', description: '',
  declared_value: '', weight: '', shipping_fee: '',
};

const Parcels: React.FC = () => {
  const [cancelTarget, setCancelTarget] = useState<Parcel | null>(null);
  const [dispatchTarget, setDispatchTarget] = useState<Parcel | null>(null);
  const [historyTarget, setHistoryTarget] = useState<Parcel | null>(null);
  const [dispatchTrip, setDispatchTrip] = useState('');
  const { data: parcels, loading, refetch } = usePaginatedResource<Parcel>('/parcels/');

  const [editing, setEditing] = useState<Parcel | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<ParcelForm>(emptyParcel);

  // Trips (for the number-plate dispatch picker) + stop names (for the from/to datalist).
  const [trips, setTrips] = useState<Trip[]>([]);
  const [stops, setStops] = useState<Stop[]>([]);
  useEffect(() => {
    listTrips({ page_size: 1000 }).then(setTrips).catch(() => setTrips([]));
    listStops().then(setStops).catch(() => setStops([]));
  }, []);

  // Dispatchable trips, labelled by number plate · route · departure.
  const tripOptions = useMemo(
    () => trips
      .filter((t) => t.status === 'scheduled' || t.status === 'boarding')
      .map((t) => ({
        value: String(t.id),
        label: `${t.vehicle_reg ?? '—'} · ${t.route_name ?? `Trip #${t.id}`} · ${new Date(t.departure_time).toLocaleString()}`,
      })),
    [trips],
  );
  const stopNames = useMemo(() => stops.map((s) => s.name), [stops]);
  const stopOptions = useMemo(
    () => stopNames.map((n) => ({ value: n, label: n })),
    [stopNames],
  );
  // Keep a saved location that is no longer in the stops list selectable when
  // editing an older parcel, so it still displays.
  const withCurrent = (opts: { value: string; label: string }[], current: string) =>
    current && !opts.some((o) => o.value === current)
      ? [{ value: current, label: current }, ...opts]
      : opts;

  const setStatus = useResourceMutation(
    async ({ id, status, trip }: { id: number; status: ParcelStatus; trip?: number }) => {
      await api.post(`/parcels/${id}/update_status/`, { status, ...(trip ? { trip } : {}) });
    },
    { successMessage: 'Status updated' }
  );

  const saveParcel = useResourceMutation(
    async (state: ParcelForm) => {
      const payload = {
        sender_name: state.sender_name,
        sender_phone: state.sender_phone,
        sender_id_number: state.sender_id_number,
        sender_kra_pin: state.sender_kra_pin,
        recipient_name: state.recipient_name,
        recipient_phone: state.recipient_phone,
        from_location: state.from_location,
        to_location: state.to_location,
        trip: state.trip ? parseInt(state.trip, 10) : null,
        description: state.description,
        declared_value: state.declared_value,
        weight: state.weight || null,
        shipping_fee: state.shipping_fee,
      };
      return editing ? updateParcel(editing.id, payload) : createParcel(payload);
    },
    { successMessage: editing ? 'Parcel updated' : 'Parcel created' }
  );

  const advance = async (p: Parcel) => {
    const next = NEXT_STATUS[p.status];
    if (!next) return;
    // Dispatch must assign a trip — open the dispatch dialog instead.
    if (next.status === 'in_transit') {
      setDispatchTarget(p);
      setDispatchTrip(p.trip ? String(p.trip) : '');
      return;
    }
    try {
      await setStatus.run({ id: p.id, status: next.status });
      refetch();
    } catch { /* toast */ }
  };

  const handleDispatch = async () => {
    if (!dispatchTarget) return;
    const tripId = parseInt(dispatchTrip, 10);
    if (!tripId) return;
    try {
      await setStatus.run({ id: dispatchTarget.id, status: 'in_transit', trip: tripId });
      setDispatchTarget(null);
      refetch();
    } catch { /* toast */ }
  };

  const handleCancel = async () => {
    if (!cancelTarget) return;
    try {
      await setStatus.run({ id: cancelTarget.id, status: 'cancelled' });
      setCancelTarget(null);
      refetch();
    } catch { /* toast */ }
  };

  const openCreate = () => { setEditing(null); setForm(emptyParcel); setShowForm(true); };
  const openEdit = (p: Parcel) => {
    setEditing(p);
    setForm({
      sender_name: p.sender_name, sender_phone: p.sender_phone,
      sender_id_number: p.sender_id_number ?? '', sender_kra_pin: p.sender_kra_pin ?? '',
      recipient_name: p.recipient_name, recipient_phone: p.recipient_phone,
      from_location: p.from_location ?? '', to_location: p.to_location ?? '',
      trip: p.trip ? String(p.trip) : '', description: p.description,
      declared_value: p.declared_value,
      weight: p.weight ?? '',
      shipping_fee: p.shipping_fee,
    });
    setShowForm(true);
  };
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try { await saveParcel.run(form); setShowForm(false); refetch(); }
    catch { /* toast */ }
  };

  const columns: Column<Parcel>[] = [
    { key: 'tracking_code',    header: 'Tracking', sortable: true },
    { key: 'from_location',    header: 'From → To',
      render: (p) => `${p.from_location || '—'} → ${p.to_location || '—'}` },
    { key: 'sender_name',      header: 'Sender' },
    { key: 'recipient_name',   header: 'Recipient' },
    { key: 'recipient_phone',  header: 'Recipient phone' },
    { key: 'shipping_fee',     header: 'Fee', align: 'right',
      render: (p) => `KES ${Number(p.shipping_fee).toLocaleString()}` },
    { key: 'status',           header: 'Status', filterable: true, filterOptions: [
      { value: 'received',   label: 'Received' },
      { value: 'in_transit', label: 'In transit' },
      { value: 'arrived',    label: 'Arrived' },
      { value: 'delivered',  label: 'Delivered' },
      { value: 'picked',     label: 'Picked' },
      { value: 'cancelled',  label: 'Cancelled' },
    ], render: (p) => <StatusBadge variant="parcel" status={p.status} /> },
    { key: 'created_at',       header: 'Created', sortable: true,
      render: (p) => new Date(p.created_at).toLocaleDateString() },
  ];

  const rowActions = (p: Parcel) => {
    const next = NEXT_STATUS[p.status];
    const canCancel = ['received', 'in_transit', 'arrived'].includes(p.status);
    return (
      <div className="inline-flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => openEdit(p)}>Edit</Button>
        <Button variant="ghost" size="sm" onClick={() => setHistoryTarget(p)}>History</Button>
        {next && (
          <Button size="sm" onClick={() => advance(p)} loading={setStatus.loading}>
            {next.label}
          </Button>
        )}
        {canCancel && (
          <Button variant="danger" size="sm" onClick={() => setCancelTarget(p)}>
            Cancel
          </Button>
        )}
      </div>
    );
  };

  const stats = useMemo(() => {
    const received = parcels.filter((p) => p.status === 'received').length;
    const inTransit = parcels.filter((p) => p.status === 'in_transit').length;
    const delivered = parcels.filter((p) => p.status === 'delivered').length;
    const cancelled = parcels.filter((p) => p.status === 'cancelled').length;
    return { received, inTransit, delivered, cancelled };
  }, [parcels]);

  return (
    <>
      <PageHeader
        title="Parcels"
        description="Parcels received, in transit, and delivered."
        actions={
          <Button onClick={openCreate} icon={<Plus className="h-4 w-4" />}>
            New parcel
          </Button>
        }
      />

      <StatStrip
        className="mb-6"
        stats={[
          { label: 'Received', value: stats.received, icon: <Package className="h-4 w-4" /> },
          { label: 'In transit', value: stats.inTransit, icon: <Truck className="h-4 w-4" /> },
          { label: 'Delivered', value: stats.delivered, icon: <CheckCircle2 className="h-4 w-4" />, tone: 'income' },
          { label: 'Cancelled', value: stats.cancelled, icon: <Ban className="h-4 w-4" /> },
        ]}
      />

      <DataTable<Parcel>
        columns={columns}
        data={parcels}
        rowKey="id"
        loading={loading}
        emptyTitle="No parcels yet"
        emptyDescription="Parcels you accept will show up here with a tracking code."
        emptyAction={
          <Button onClick={openCreate} icon={<Plus className="h-4 w-4" />}>
            New parcel
          </Button>
        }
        actions={rowActions}
      />

      <Modal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        title={editing ? 'Edit parcel' : 'New parcel'}
        description={editing ? 'Update parcel details.' : 'Register a new parcel for shipment.'}
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowForm(false)} disabled={saveParcel.loading}>Cancel</Button>
            <Button onClick={handleSave} loading={saveParcel.loading}>{editing ? 'Save changes' : 'Create parcel'}</Button>
          </>
        }
      >
        <form onSubmit={handleSave} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="Sender name" name="sender_name" required error={saveParcel.fieldErrors.sender_name}>
            <Input id="sender_name" required value={form.sender_name}
              onChange={(e) => setForm({ ...form, sender_name: e.target.value })} />
          </FormField>
          <FormField label="Sender phone" name="sender_phone" required error={saveParcel.fieldErrors.sender_phone}>
            <Input id="sender_phone" type="tel" required value={form.sender_phone}
              onChange={(e) => setForm({ ...form, sender_phone: e.target.value })} />
          </FormField>
          <FormField label="Sender ID number" name="sender_id_number" hint="Optional" error={saveParcel.fieldErrors.sender_id_number}>
            <Input id="sender_id_number" value={form.sender_id_number}
              onChange={(e) => setForm({ ...form, sender_id_number: e.target.value })} />
          </FormField>
          <FormField label="Sender KRA PIN" name="sender_kra_pin" hint="Optional — for tax invoice" error={saveParcel.fieldErrors.sender_kra_pin}>
            <Input id="sender_kra_pin" value={form.sender_kra_pin}
              onChange={(e) => setForm({ ...form, sender_kra_pin: e.target.value })} />
          </FormField>
          <FormField label="Recipient name" name="recipient_name" required error={saveParcel.fieldErrors.recipient_name}>
            <Input id="recipient_name" required value={form.recipient_name}
              onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} />
          </FormField>
          <FormField label="Recipient phone" name="recipient_phone" required error={saveParcel.fieldErrors.recipient_phone}>
            <Input id="recipient_phone" type="tel" required value={form.recipient_phone}
              onChange={(e) => setForm({ ...form, recipient_phone: e.target.value })} />
          </FormField>
          <FormField label="From" name="from_location" required hint="Pick a stop" error={saveParcel.fieldErrors.from_location}>
            <SearchableSelect
              id="from_location"
              options={withCurrent(stopOptions, form.from_location)}
              value={form.from_location}
              onChange={(v) => setForm({ ...form, from_location: v })}
              placeholder="Select a stop…"
              searchPlaceholder="Search stops…"
              aria-label="From stop"
            />
          </FormField>
          <FormField label="To" name="to_location" required hint="Pick a stop" error={saveParcel.fieldErrors.to_location}>
            <SearchableSelect
              id="to_location"
              options={withCurrent(stopOptions, form.to_location)}
              value={form.to_location}
              onChange={(v) => setForm({ ...form, to_location: v })}
              placeholder="Select a stop…"
              searchPlaceholder="Search stops…"
              aria-label="To stop"
            />
          </FormField>
          <FormField label="Transport" name="trip" hint="Optional — assigned when you dispatch the parcel." error={saveParcel.fieldErrors.trip}>
            <SearchableSelect
              id="trip"
              options={tripOptions}
              value={form.trip}
              onChange={(v) => setForm({ ...form, trip: v })}
              placeholder="Select by number plate…"
              searchPlaceholder="Search by number plate…"
              aria-label="Transport"
            />
          </FormField>
          <FormField label="Description" name="description" required error={saveParcel.fieldErrors.description}>
            <Input id="description" required value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </FormField>
          <FormField label="Declared value (KES)" name="declared_value" required error={saveParcel.fieldErrors.declared_value}>
            <Input id="declared_value" type="number" step="0.01" required value={form.declared_value}
              onChange={(e) => setForm({ ...form, declared_value: e.target.value })} />
          </FormField>
          <FormField label="Weight kg" name="weight" hint="Optional" error={saveParcel.fieldErrors.weight}>
            <Input id="weight" type="number" step="0.01" value={form.weight}
              onChange={(e) => setForm({ ...form, weight: e.target.value })} />
          </FormField>
          <FormField label="Shipping fee (KES)" name="shipping_fee" required error={saveParcel.fieldErrors.shipping_fee}>
            <Input id="shipping_fee" type="number" step="0.01" required value={form.shipping_fee}
              onChange={(e) => setForm({ ...form, shipping_fee: e.target.value })} />
          </FormField>
        </form>
      </Modal>

      <Modal
        isOpen={!!dispatchTarget}
        onClose={() => setDispatchTarget(null)}
        title="Dispatch parcel"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setDispatchTarget(null)} disabled={setStatus.loading}>Cancel</Button>
            <Button loading={setStatus.loading} disabled={!dispatchTrip} onClick={handleDispatch}>Dispatch</Button>
          </>
        }
      >
        <FormField label="Transport" name="dispatch-trip" required hint="Pick the bus by number plate.">
          <SearchableSelect
            id="dispatch-trip"
            options={tripOptions}
            value={dispatchTrip}
            onChange={setDispatchTrip}
            placeholder="Select by number plate…"
            searchPlaceholder="Search by number plate…"
            aria-label="Transport"
          />
        </FormField>
      </Modal>

      {historyTarget && (
        <ParcelHistoryModal
          parcelId={historyTarget.id}
          trackingCode={historyTarget.tracking_code}
          onClose={() => setHistoryTarget(null)}
        />
      )}

      <ConfirmDialog
        isOpen={!!cancelTarget}
        title="Cancel parcel?"
        message={`Cancel parcel ${cancelTarget?.tracking_code ?? ''}? This cannot be undone.`}
        confirmLabel="Cancel parcel"
        variant="danger"
        loading={setStatus.loading}
        onConfirm={handleCancel}
        onCancel={() => setCancelTarget(null)}
      />
    </>
  );
};

export default Parcels;
